import { useState } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { INQ_STATUS as STATUSES, inqStatus } from "@/components/admin/inquiryStatus";
import { InquiryNotes } from "@/components/admin/InquiryNotes";

const label = (v) => inqStatus(v).label;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const Row = ({ k, v }) => (
  <div className="border-b border-[#2E2A24] py-3">
    <p className="font-mono-rc text-[11px] uppercase tracking-[0.18em] text-zinc-400">{k}</p>
    <p className="mt-1 whitespace-pre-wrap text-sm text-[#F5EFE6]">{v || "—"}</p>
  </div>
);

const StatusSelect = ({ value, onChange, testId, className = "" }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onClick={(e) => e.stopPropagation()}
    aria-label="Inquiry status"
    className={`cursor-pointer appearance-none rounded-full px-3 py-1 text-xs outline-none ${inqStatus(value).cls} ${className}`}
    data-testid={testId}
  >
    {STATUSES.map((s) => <option key={s.v} value={s.v} className="bg-[#121215] text-[#F5EFE6]">{s.label}</option>)}
  </select>
);

const Filters = ({ items, filter, setFilter }) => (
  <div className="mb-4 flex flex-wrap gap-2" data-testid="inquiry-filters">
    {[{ v: "all", label: "All" }, ...STATUSES].map((s) => {
      const n = s.v === "all" ? items.length : items.filter((i) => i.status === s.v).length;
      return (
        <button key={s.v} onClick={() => setFilter(s.v)} className={`rounded-full border px-4 py-1.5 text-sm transition-[background-color,border-color] ${filter === s.v ? "border-[#C4832B] bg-[#C4832B] text-[#0A0A0C]" : "border-[#2E2A24] text-zinc-300 hover:border-[#C4832B]"}`} data-testid={`inquiry-filter-${s.v}`}>
          {s.label} <span className="ml-1 opacity-70">{n}</span>
        </button>
      );
    })}
  </div>
);

const Detail = ({ item, setItems, onStatus, onDelete, onMakeClient, onAcceptDate, canMakeClient }) => (
  <div className="max-h-[70vh] overflow-y-auto pr-1">
    <div className="flex flex-wrap items-center gap-3 pb-3">
      <StatusSelect value={item.status} onChange={(v) => onStatus(item.id, v)} className="py-2 text-sm" testId="inquiry-status-select" />
      <a href={`mailto:${item.email}`} className="rounded-full bg-[#C4832B] px-4 py-2 text-sm font-medium text-[#0A0A0C]" data-testid="inquiry-reply-link">Reply by email</a>
      {canMakeClient && <button onClick={() => onMakeClient(item.id)} className="rounded-full border border-[#2E2A24] px-4 py-2 text-sm hover:border-[#C4832B]" data-testid="inquiry-make-client-btn">Add as client</button>}
      {item.event_date && !item.date_accepted && (
        <button onClick={() => onAcceptDate(item.id)} className="rounded-full border border-[#C4832B] px-4 py-2 text-sm text-[#E09838] hover:bg-[#C4832B]/10" data-testid="inquiry-accept-date-btn">Accept date &amp; black out</button>
      )}
      <button onClick={() => onDelete(item.id)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300" data-testid="inquiry-delete-btn">
        <Trash2 className="h-4 w-4" /> Delete
      </button>
    </div>
    <InquiryNotes item={item} setItems={setItems} />
    <Row k="Email" v={item.email} />
    <Row k="Phone" v={item.phone} />
    <Row k="Organization" v={item.organization} />
    <Row k="Service" v={item.service} />
    <Row k="Requested event date" v={item.event_date ? `${new Date(item.event_date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" })}${item.date_accepted ? " — accepted" : ""}` : ""} />
    <Row k="Timeline / event date" v={item.timeline} />
    <Row k="Budget" v={item.budget} />
    <Row k="Project description" v={item.description} />
    <Row k="Additional details" v={item.additional} />
    <Row k="Received" v={`${fmt(item.created_at)} · email notification ${item.email_status} · thank-you email ${item.autoreply_status ?? "not sent"}`} />
  </div>
);

export const InquiriesPanel = ({ items, setItems, onClientCreated, canMakeClient }) => {
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const open = items.find((i) => i.id === openId);
  const shown = filter === "all" ? items : items.filter((i) => i.status === filter);

  const onAcceptDate = async (id) => {
    try {
      await api.post(`/admin/inquiries/${id}/accept-date`);
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, date_accepted: true, status: "booked" } : x)));
      toast.success("Date accepted and blacked out on the calendar");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onMakeClient = async (id) => {
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/client`);
      toast.success(`${data.name} is now a client`);
      setOpenId(null);
      onClientCreated(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${label(status)}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      setOpenId(null);
      toast.success("Inquiry deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!items.length) {
    return <p className="rounded-2xl border border-dashed border-[#2E2A24] p-10 text-center text-zinc-400" data-testid="inquiries-empty">No inquiries yet. They'll appear here as soon as someone submits the form.</p>;
  }

  return (
    <>
      <Filters items={items} filter={filter} setFilter={setFilter} />
      {!shown.length && <p className="rounded-2xl border border-dashed border-[#2E2A24] p-10 text-center text-zinc-400" data-testid="inquiries-filter-empty">No inquiries marked {label(filter)}.</p>}
      <div className="divide-y divide-[#2E2A24] overflow-hidden rounded-2xl border border-[#2E2A24]" data-testid="inquiries-list">
        {shown.map((i) => (
          <div key={i.id} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 bg-[#121215] px-5 py-4 transition-[background-color] hover:bg-[#1A1A20] sm:grid-cols-12 sm:gap-4" data-testid={`inquiry-row-${i.id}`}>
            <button onClick={() => setOpenId(i.id)} className="grid grid-cols-1 gap-1 text-left sm:col-span-10 sm:grid-cols-10 sm:items-center sm:gap-4" data-testid={`inquiry-open-${i.id}`}>
              <span className="sm:col-span-3">
                <span className="block font-medium text-[#F5EFE6]">{i.full_name}</span>
                <span className="block text-xs text-zinc-400">{i.organization || i.email}</span>
              </span>
              <span className="text-sm text-zinc-300 sm:col-span-4">{i.service}</span>
              <span className="flex items-center gap-2 text-xs text-zinc-400 sm:col-span-3">
                {fmt(i.created_at)}
                {i.notes?.length > 0 && <span className="inline-flex items-center gap-1 text-zinc-300" data-testid={`inquiry-note-count-${i.id}`}><MessageSquare className="h-3.5 w-3.5" />{i.notes.length}</span>}
              </span>
            </button>
            <span className="sm:col-span-2 sm:text-right">
              <StatusSelect value={i.status} onChange={(v) => onStatus(i.id, v)} testId={`inquiry-row-status-${i.id}`} />
            </span>
          </div>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-2xl border-[#2E2A24] bg-[#121215]" data-testid="inquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">{open?.full_name}</DialogTitle>
          </DialogHeader>
          {open && <Detail item={open} setItems={setItems} onStatus={onStatus} onDelete={onDelete} onMakeClient={onMakeClient} onAcceptDate={onAcceptDate} canMakeClient={canMakeClient} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
